import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  VStack,
  HStack,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import { FiPlus, FiEdit2, FiTrash2, FiPlay } from "react-icons/fi";
import Navbar from "../../Components/Navbar/Navbar";
import Sidebar from "../../Components/Sidebar";
import axios from "axios";
import { api_url } from "../../url";

const SIDEBAR_WIDTH = "240px";

const BonusSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingSetting, setEditingSetting] = useState(null);
  const toast = useToast();

  const [formData, setFormData] = useState({
    type: "registration",
    name: "",
    description: "",
    rewardType: "fixed",
    rewardAmount: 0,
    minRequirementAmount: 100,
    maxBonusLimit: 25000,
    startDate: "",
    endDate: "",
    isActive: true,
    referralRewards: {
      referrerAmount: 0,
      refereeAmount: 0,
    },
    weeklyLossPercentage: 0,
  });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${api_url}/bonus`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(res.data.settings);
    } catch (error) {
      toast({
        title: "Error fetching settings",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: Number(value) },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: name === "isActive" ? e.target.checked : value,
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      if (editingSetting) {
        await axios.patch(`${api_url}/bonus/${editingSetting._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast({ title: "Setting updated", status: "success" });
      } else {
        await axios.post(`${api_url}/bonus`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast({ title: "Setting created", status: "success" });
      }
      onClose();
      fetchSettings();
    } catch (error) {
      toast({
        title: "Error saving setting",
        description: error.response?.data?.message || error.message,
        status: "error",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${api_url}/bonus/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "Setting deleted", status: "success" });
      fetchSettings();
    } catch (error) {
      toast({ title: "Delete failed", status: "error" });
    }
  };

  const handleTriggerWeekly = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${api_url}/bonus/trigger-weekly`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "Weekly bonus processing started", status: "info" });
    } catch (error) {
      toast({ title: "Trigger failed", status: "error" });
    }
  };

  const openAddModal = () => {
    setEditingSetting(null);
    setFormData({
      type: "registration",
      name: "",
      description: "",
      rewardType: "fixed",
      rewardAmount: 0,
      minRequirementAmount: 100,
      maxBonusLimit: 25000,
      startDate: "",
      endDate: "",
      isActive: true,
      referralRewards: {
        referrerAmount: 0,
        refereeAmount: 0,
      },
      weeklyLossPercentage: 0,
    });
    onOpen();
  };

  const openEditModal = (setting) => {
    setEditingSetting(setting);
    setFormData({
      ...setting,
      startDate: setting.startDate ? setting.startDate.split("T")[0] : "",
      endDate: setting.endDate ? setting.endDate.split("T")[0] : "",
    });
    onOpen();
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        p={{ base: 4, md: 8 }}
        bg="#f8f9fd"
        minH="100vh"
        mt="80px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        <Flex justify="space-between" align="center" mb={6}>
          <Text fontSize="2xl" fontWeight="700" color="#344767">
            Bonus System Settings
          </Text>
          <HStack spacing={4}>
            <Button
              leftIcon={<FiPlay />}
              colorScheme="orange"
              onClick={handleTriggerWeekly}
            // size="sm"
            >
              Trigger Weekly Loss Bonus (Manual)
            </Button>
            <Button
              leftIcon={<FiPlus />}
              bg="#4c3cff"
              color="white"
              onClick={openAddModal}
              _hover={{ bg: "#3a2bd1" }}
            >
              Add New Bonus
            </Button>
          </HStack>
        </Flex>

        <Box bg="white" borderRadius="12px" shadow="sm" overflowX="auto">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Type</Th>
                <Th>Name</Th>
                <Th>Reward</Th>
                <Th>Status</Th>
                <Th>Dates</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {settings.map((s) => (
                <Tr key={s._id}>
                  <Td>
                    <Badge colorScheme="blue" textTransform="capitalize">
                      {s.type.replace("_", " ")}
                    </Badge>
                  </Td>
                  <Td fontWeight="600">{s.name}</Td>
                  <Td>
                    {s.type === "registration" ? (
                      <VStack align="start" spacing={0} fontSize="xs">
                        <Text>Referrer: ₹{s.referralRewards?.referrerAmount}</Text>
                        <Text>Referee: ₹{s.referralRewards?.refereeAmount}</Text>
                      </VStack>
                    ) : s.type === "weekly_loss" ? (
                      <Text>{s.weeklyLossPercentage}% of Loss</Text>
                    ) : (
                      <Text>
                        {s.rewardType === "fixed" ? `₹${s.rewardAmount}` : `${s.rewardAmount}%`}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={s.isActive ? "green" : "red"}>
                      {s.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </Td>
                  <Td fontSize="xs">
                    {s.startDate ? new Date(s.startDate).toLocaleDateString() : "No start"} -{" "}
                    {s.endDate ? new Date(s.endDate).toLocaleDateString() : "No end"}
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        icon={<FiEdit2 />}
                        size="sm"
                        aria-label="Edit"
                        onClick={() => openEditModal(s)}
                      />
                      <IconButton
                        icon={<FiTrash2 />}
                        size="sm"
                        colorScheme="red"
                        aria-label="Delete"
                        onClick={() => handleDelete(s._id)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {settings.length === 0 && (
                <Tr>
                  <Td colSpan={6} textAlign="center">No bonus settings found</Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingSetting ? "Edit Bonus" : "Add New Bonus"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Bonus Type</FormLabel>
                <Select name="type" value={formData.type} onChange={handleInputChange}>
                  <option value="registration">Registration (Referral)</option>
                  <option value="deposit">Deposit Bonus</option>
                  <option value="weekly_loss">Weekly Player Loss (GGR)</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input name="name" value={formData.name} onChange={handleInputChange} />
              </FormControl>

              {formData.type === "registration" ? (
                <>
                  <FormControl>
                    <FormLabel>Referrer Reward (₹)</FormLabel>
                    <Input
                      name="referralRewards.referrerAmount"
                      type="number"
                      value={formData.referralRewards.referrerAmount}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Referee Reward (₹)</FormLabel>
                    <Input
                      name="referralRewards.refereeAmount"
                      type="number"
                      value={formData.referralRewards.refereeAmount}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </>
              ) : formData.type === "weekly_loss" ? (
                <FormControl>
                  <FormLabel>Loss Percentage (%)</FormLabel>
                  <Input
                    name="weeklyLossPercentage"
                    type="number"
                    value={formData.weeklyLossPercentage}
                    onChange={handleInputChange}
                  />
                </FormControl>
              ) : (
                <>
                  <FormControl>
                    <FormLabel>Reward Type</FormLabel>
                    <Select name="rewardType" value={formData.rewardType} onChange={handleInputChange}>
                      <option value="fixed">Fixed Amount</option>
                      <option value="percentage">Percentage</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Reward Amount / %</FormLabel>
                    <Input
                      name="rewardAmount"
                      type="number"
                      value={formData.rewardAmount}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </>
              )}

              <HStack w="full">
                <FormControl>
                  <FormLabel>Min Requirement (₹)</FormLabel>
                  <Input
                    name="minRequirementAmount"
                    type="number"
                    value={formData.minRequirementAmount}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Max Bonus Limit (₹)</FormLabel>
                  <Input
                    name="maxBonusLimit"
                    type="number"
                    value={formData.maxBonusLimit}
                    onChange={handleInputChange}
                  />
                </FormControl>
              </HStack>

              <HStack w="full">
                <FormControl>
                  <FormLabel>Start Date</FormLabel>
                  <Input
                    name="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>End Date</FormLabel>
                  <Input
                    name="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={handleInputChange}
                  />
                </FormControl>
              </HStack>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Active Status</FormLabel>
                <Switch name="isActive" isChecked={formData.isActive} onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {editingSetting ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BonusSettings;
